import { isArray, isEmpty, mergeWith, union } from "lodash";
import defaultConfig from "config/appConfigFiles/default.json";
import georgeConfig from "config/appConfigFiles/george.json";
import { SUBSCRIPTION_CONFIGS } from "config/subscriptionConfigs";
import { AppConfig, AppConfigOverride } from "types/appFunctionality";

export const replaceConfigArrays = (_previous: unknown, next: unknown) =>
  isArray(next) ? next : undefined;

export const mergeLegacyConfigArrays = (previous: unknown, next: unknown) => {
  if (isArray(next) && isEmpty(next)) return next;
  if (isArray(previous) && isArray(next)) return union(previous, next);
};

export function getGeorgeSubscriptionConfig(subscriptionType?: string): AppConfig {
  const { subscriptionOverwrites, ...base } = georgeConfig;
  return mergeWith(
    {},
    base,
    subscriptionOverwrites,
    getGeorgeSubscriptionOverride(subscriptionType),
    replaceConfigArrays,
  ) as AppConfig;
}

export function getGeorgeSubscriptionOverride(
  subscriptionType?: string,
): AppConfigOverride | undefined {
  const definition = getSubscriptionConfigDefinition(subscriptionType);
  return definition?.brands.includes("george") ? definition.config : undefined;
}

export function getSubscriptionConfigDefinition(subscriptionType?: string) {
  return subscriptionType && Object.prototype.hasOwnProperty.call(SUBSCRIPTION_CONFIGS, subscriptionType)
    ? SUBSCRIPTION_CONFIGS[subscriptionType]
    : undefined;
}

// A role may restrict available features, but cannot enable features hidden by the profile.
function mergeGeorgeRole(previous: unknown, next: unknown) {
  if (isArray(next)) return next;
  if (previous === "hidden" && (next === "enabled" || next === "disabled")) return previous;
  if (previous === "disabled" && next === "enabled") return previous;
}

export function composeAppConfig(
  internalAppType: string,
  subscriptionConfig?: AppConfigOverride,
  roleConfig?: AppConfigOverride,
): AppConfig {
  const isGeorge = internalAppType === "george" || subscriptionConfig?.baseConfig === "george";
  if (isGeorge) {
    const profile = mergeWith(
      {},
      getGeorgeSubscriptionConfig(),
      subscriptionConfig?.subscriptionOverwrites ?? {},
      subscriptionConfig ?? {},
      replaceConfigArrays,
    );
    return mergeWith({}, profile, roleConfig ?? {}, mergeGeorgeRole) as AppConfig;
  }
  // Preserve the established merge order and array semantics for existing subscriptions.
  const profile = mergeWith(
    {},
    defaultConfig,
    subscriptionConfig ?? {},
    roleConfig ?? {},
    mergeLegacyConfigArrays,
  );
  return mergeWith(
    {},
    profile,
    subscriptionConfig?.subscriptionOverwrites ?? {},
    replaceConfigArrays,
  ) as AppConfig;
}
